import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, AlertCircle, User, BellRing, Power, DollarSign, Star, MessageCircle, Clock, Send, TrendingUp, Users, Activity } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import ChatInterface from '../chat/ChatInterface';
import { registerServiceWorker, subscribeToPushNotifications, requestNotificationPermission } from '../../lib/push-notifications';
import NotificationSoundSelector from './NotificationSoundSelector';
import AdvisorProfile from './AdvisorProfile';

interface ChatRequest {
  id: string;
  client_id: string;
  client_name: string;
  created_at: string;
  status: 'pending' | 'active';
  isProcessing?: boolean;
}

interface AdvisorStats {
  totalEarnings: number;
  pendingBalance: number;
  availableBalance: number;
  totalReadings: number;
  rating: number;
  monthlyEarnings: number;
  averageRating: number;
  activeClients: number;
  totalClients: number;
}

interface AdvisorProfile {
  displayName: string;
  specialty: string;
  bio: string;
  perMessageRate: number;
  experienceYears: number;
  profileImageUrl: string | null;
  rating: number;
  totalReadings: number;
}

interface AdvisorSettings {
  is_available: boolean;
  per_message_rate: number;
  display_name?: string;
  profile_image_url?: string;
}

function AdvisorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [chatRequests, setChatRequests] = useState<ChatRequest[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatRequest | null>(null);
  const [settings, setSettings] = useState<AdvisorSettings | null>(null);
  const [stats, setStats] = useState<AdvisorStats>({
    totalEarnings: 0,
    pendingBalance: 0,
    availableBalance: 0,
    totalReadings: 0,
    rating: 0,
    monthlyEarnings: 0,
    averageRating: 0,
    activeClients: 0,
    totalClients: 0
  });
  const [profile, setProfile] = useState<AdvisorProfile | null>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [notificationSound, setNotificationSound] = useState<string>('https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const channelRef = useRef<any>(null);
  const currentlyPlayingRef = useRef<boolean>(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [minimizedChat, setMinimizedChat] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [newPricing, setNewPricing] = useState({
    perMessageRate: 0
  });
  const [activeTab, setActiveTab] = useState<'dashboard' | 'profile'>('dashboard');
  const [showChatModal, setShowChatModal] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error('No user found');
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profileData) {
        setProfile({
          displayName: profileData.display_name,
          specialty: profileData.specialty || '',
          bio: profileData.bio || '',
          perMessageRate: profileData.per_message_rate || 0,
          experienceYears: profileData.experience_years || 0,
          profileImageUrl: profileData.profile_image_url,
          rating: profileData.rating || 0,
          totalReadings: profileData.total_readings || 0
        });
        
        setNewPricing({
          perMessageRate: profileData.per_message_rate || 0
        });
      }

      const { data: settingsData, error: settingsError } = await supabase
        .from('advisor_settings')
        .select('*')
        .eq('advisor_id', user.id)
        .maybeSingle();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;
      
      if (settingsData) {
        setSettings(settingsData);
      }

      const { data: walletData, error: walletError } = await supabase
        .from('advisor_wallets')
        .select('*')
        .eq('advisor_id', user.id)
        .single();

      if (walletError) throw walletError;

      if (walletData) {
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);

        const { data: monthlyData, error: monthlyError } = await supabase
          .from('transactions')
          .select('advisor_earnings')
          .eq('advisor_id', user.id)
          .gte('created_at', startOfMonth.toISOString())
          .not('advisor_earnings', 'is', null);

        if (monthlyError) throw monthlyError;

        const monthlyEarnings = monthlyData?.reduce((sum, transaction) => 
          sum + (transaction.advisor_earnings || 0), 0) || 0;

        setStats({
          totalEarnings: walletData.total_earned || 0,
          pendingBalance: walletData.pending_balance || 0,
          availableBalance: walletData.available_balance || 0,
          totalReadings: profileData.total_readings || 0,
          rating: profileData.rating || 0,
          monthlyEarnings,
          averageRating: profileData.rating || 0,
          activeClients: 0,
          totalClients: 0
        });
      }

      const { data: chatData, error: chatError } = await supabase
        .from('conversations')
        .select(`
          id,
          client_id,
          status,
          created_at,
          profiles:client_id (
            display_name
          )
        `)
        .eq('advisor_id', user.id)
        .in('status', ['pending', 'active'])
        .order('created_at', { ascending: false });

      if (chatError) throw chatError;

      if (chatData) {
        const formattedChats = chatData.map(chat => ({
          id: chat.id,
          client_id: chat.client_id,
          client_name: chat.profiles?.display_name || 'Anonymous',
          created_at: chat.created_at,
          status: chat.status as 'pending' | 'active',
          isProcessing: false
        }));
        
        setChatRequests(formattedChats);
      }

      const { data: userSettings, error: userSettingsError } = await supabase
        .from('user_settings')
        .select('notification_sound')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!userSettingsError && userSettings?.notification_sound) {
        setNotificationSound(userSettings.notification_sound);
      } else {
        setNotificationSound('https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3');
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const playNotificationSoundEffect = async () => {
    try {
      if (currentlyPlayingRef.current) {
        return;
      }
      
      currentlyPlayingRef.current = true;
      
      const soundUrl = notificationSound || 'https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3';
      console.log('Playing notification sound:', soundUrl);
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      const audio = new Audio(soundUrl);
      audio.volume = 0.5;
      audioRef.current = audio;
      
      audio.onended = () => {
        currentlyPlayingRef.current = false;
      };
      
      try {
        await audio.play();
      } catch (playError) {
        console.error('Error playing sound:', playError);
        currentlyPlayingRef.current = false;
        
        try {
          const fallbackSound = 'https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3';
          console.log('Trying fallback sound:', fallbackSound);
          const fallbackAudio = new Audio(fallbackSound);
          fallbackAudio.volume = 0.5;
          audioRef.current = fallbackAudio;
          await fallbackAudio.play();
        } catch (fallbackError) {
          console.error('Fallback sound also failed:', fallbackError);
          
          const alternativeSounds = [
            '/sounds/bell.mp3',
            '/sounds/chime.mp3',
            '/sounds/ding.mp3',
            '/sounds/soft.mp3'
          ];

          for (const soundPath of alternativeSounds) {
            try {
              const alternativeAudio = new Audio(soundPath);
              alternativeAudio.volume = 0.5;
              audioRef.current = alternativeAudio;
              await alternativeAudio.play();
              break;
            } catch (altError) {
              console.warn(`Alternative sound ${soundPath} failed:`, altError);
              continue;
            }
          }
        }
      }
    } catch (err) {
      console.error('Error playing notification sound:', err);
      currentlyPlayingRef.current = false;
    }
  };

  const stopNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      currentlyPlayingRef.current = false;
    }
  };

  const handleConversationChange = (payload: any) => {
    console.log('Conversation change:', payload);
    
    if (payload.eventType === 'INSERT' && payload.new.status === 'pending') {
      playNotificationSoundEffect();
    }
    
    fetchDashboardData();
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchDashboardData();
    setupNotifications();

    const channel = supabase.channel('advisor-dashboard')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `advisor_id=eq.${user.id}`,
        },
        handleConversationChange
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        
        if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
          console.log('Subscription error, attempting to reconnect...');
          
          setTimeout(() => {
            if (user) {
              const newChannel = supabase.channel('advisor-dashboard-retry')
                .on(
                  'postgres_changes',
                  {
                    event: '*',
                    schema: 'public',
                    table: 'conversations',
                    filter: `advisor_id=eq.${user.id}`,
                  },
                  handleConversationChange
                )
                .subscribe();
                
              channelRef.current = newChannel;
            }
          }, 2000);
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
      stopNotificationSound();
    };
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    
    const checkForChats = async () => {
      try {
        if (!selectedChat) {
          const { data, error } = await supabase
            .from('conversations')
            .select(`
              id,
              client_id,
              status,
              created_at,
              profiles:client_id (
                display_name
              )
            `)
            .eq('advisor_id', user.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (error) {
            console.error('Error checking for active chats:', error);
            return;
          }
          
          if (data && data.length > 0 && !selectedChat) {
            const chat = data[0];
            setSelectedChat({
              id: chat.id,
              client_id: chat.client_id,
              client_name: chat.profiles?.display_name || 'Anonymous',
              created_at: chat.created_at,
              status: 'active',
              isProcessing: false
            });
            setShowChatModal(true);
          }
        }
      } catch (err) {
        console.error('Error in chat check interval:', err);
      }
    };
    
    checkForChats();
    const intervalId = setInterval(checkForChats, 10000);
    
    return () => clearInterval(intervalId);
  }, [user, selectedChat]);

  const setupNotifications = async () => {
    try {
      if (!('Notification' in window)) {
        setNotificationError('This browser does not support notifications');
        setNotificationsEnabled(false);
        return;
      }

      if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
        setNotificationError(null);
        
        const registration = await registerServiceWorker();
        await subscribeToPushNotifications(registration);
        return;
      }
      
      if (Notification.permission === 'denied') {
        setNotificationError('Notifications are disabled. Please enable them in your browser settings to receive chat requests.');
        setNotificationsEnabled(false);
        return;
      }

      setNotificationError('Click "Test Notifications" to enable notifications');
      setNotificationsEnabled(false);
    } catch (err) {
      console.error('Error setting up notifications:', err);
      setNotificationError(err instanceof Error ? err.message : 'Failed to set up notifications');
      setNotificationsEnabled(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      setError(null);
      
      if (Notification.permission !== 'granted') {
        const permission = await requestNotificationPermission();
        
        if (permission === 'granted') {
          const registration = await registerServiceWorker();
          const subscription = await subscribeToPushNotifications(registration);
          
          if (user) {
            const { error: subscriptionError } = await supabase
              .from('push_subscriptions')
              .upsert({
                user_id: user.id,
                subscription: JSON.stringify(subscription)
              });
              
            if (subscriptionError) {
              console.error('Error saving subscription:', subscriptionError);
            }
          }
          
          setNotificationsEnabled(true);
          setNotificationError(null);
        } else {
          setNotificationError('Notification permission denied. Please enable notifications in your browser settings.');
          setNotificationsEnabled(false);
          return;
        }
      }

      await playNotificationSoundEffect();

      const notification = new Notification('Test Notification', {
        body: 'This is a test notification from Psychic Insights',
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-192x192.png',
        vibrate: [200, 100, 200, 100, 200, 100, 400],
        tag: 'test-notification',
        renotify: true
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      setSuccess('Test notification sent successfully!');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error sending test notification:', err);
      setError('Failed to send test notification. Please ensure notifications are enabled in your browser settings.');
    }
  };

  const handleChatAccept = useCallback(async (request: ChatRequest) => {
    try {
      setChatRequests(prev => prev.map(r => 
        r.id === request.id ? { ...r, isProcessing: true } : r
      ));
      
      setChatLoading(true);
      
      // Update conversation status to active
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ 
          status: 'active',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.id);

      if (updateError) {
        console.error('Error updating conversation status:', updateError);
        throw updateError;
      }

      console.log('Successfully updated conversation status to active');

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ status: 'busy' })
        .eq('id', user?.id);
        
      if (profileError) {
        console.error('Error updating advisor status:', profileError);
      }

      stopNotificationSound();
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSelectedChat(request);
      setShowChatModal(true);
      
      setChatRequests(prev => prev.map(r => 
        r.id === request.id ? { ...r, status: 'active', isProcessing: false } : r
      ));
      
      setTimeout(() => {
        setChatLoading(false);
      }, 500);
    } catch (err) {
      console.error('Error accepting chat:', err);
      setError('Failed to accept chat request');
      
      setChatRequests(prev => prev.map(r => 
        r.id === request.id ? { ...r, isProcessing: false } : r
      ));
      
      setChatLoading(false);
    }
  }, [user]);

  const handleChatDecline = useCallback(async (request: ChatRequest) => {
    try {
      setChatRequests(prev => prev.map(r => 
        r.id === request.id ? { ...r, isProcessing: true } : r
      ));
      
      // This is the only place where we should set status to 'declined'
      // and it's only done by the advisor, not the client
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ 
          status: 'declined',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.id);

      if (updateError) throw updateError;

      stopNotificationSound();
      
      setChatRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (err) {
      console.error('Error declining chat:', err);
      setError('Failed to decline chat request');
      
      setChatRequests(prev => prev.map(r => 
        r.id === request.id ? { ...r, isProcessing: false } : r
      ));
    }
  }, []);

  const toggleAvailability = async () => {
    if (!settings || !user?.id) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/toggle-advisor-availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          advisorId: user.id,
          isAvailable: !settings.is_available
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update availability');
      }
      
      console.log('Availability toggled successfully');

      setSettings(prev => prev ? {
        ...prev,
        is_available: !prev.is_available
      } : null);

      const { data: activeChats, error: chatError } = await supabase
        .from('conversations')
        .select('id')
        .eq('advisor_id', user.id)
        .eq('status', 'active')
        .limit(1);
        
      if (chatError) {
        console.error('Error checking active chats:', chatError);
      }
      
      if (!settings.is_available && activeChats && activeChats.length > 0) {
        await supabase
          .from('profiles')
          .update({ status: 'busy' })
          .eq('id', user.id);
      }

    } catch (err) {
      console.error('Error toggling availability:', err);
      setError('Failed to update availability');
    }
  };

  const handleSoundChange = (soundUrl: string) => {
    setNotificationSound(soundUrl);
  };

  const handleUpdatePricing = async () => {
    try {
      setError(null);
      
      if (newPricing.perMessageRate < 0) {
        setError('Price cannot be negative');
        return;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          per_message_rate: newPricing.perMessageRate,
          updated_at: new Date().toISOString()
        })
        .eq('id', user?.id);

      if (profileError) throw profileError;

      const { error: settingsError } = await supabase
        .from('advisor_settings')
        .update({
          per_message_rate: newPricing.perMessageRate,
          updated_at: new Date().toISOString()
        })
        .eq('advisor_id', user?.id);

      if (settingsError) {
        console.warn('Error updating advisor settings:', settingsError);
      }

      setProfile(prev => prev ? {
        ...prev,
        perMessageRate: newPricing.perMessageRate
      } : null);

      setSettings(prev => prev ? {
        ...prev,
        per_message_rate: newPricing.perMessageRate
      } : null);

      setSuccess('Pricing updated successfully!');
      setShowPricingModal(false);
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating pricing:', err);
      setError('Failed to update pricing. Please try again.');
    }
  };

  const handleProfileUpdate = () => {
    fetchDashboardData();
  };

  const handleChatClose = useCallback(async () => {
    console.log('Closing chat - clearing selected chat');
    
    if (selectedChat) {
      // Update advisor status based on availability and other active chats
      try {
        // Check if there are other active chats
        const { data: activeChats, error: chatError } = await supabase
          .from('conversations')
          .select('id')
          .eq('advisor_id', user?.id)
          .eq('status', 'active')
          .neq('id', selectedChat.id)
          .limit(1);
          
        if (chatError) {
          console.error('Error checking active chats:', chatError);
        }
        
        // If no other active chats and advisor is available, set status to online
        // Otherwise, keep as busy or set to offline based on availability
        if (!activeChats || activeChats.length === 0) {
          // Get advisor availability setting
          const { data: advisorSettings, error: settingsError } = await supabase
            .from('advisor_settings')
            .select('is_available')
            .eq('advisor_id', user?.id)
            .single();
            
          if (settingsError) {
            console.error('Error checking advisor settings:', settingsError);
          }
          
          const newStatus = advisorSettings?.is_available ? 'online' : 'offline';
          
          console.log(`Updating advisor status to ${newStatus} after chat close`);
          
          const { error: profileError } = await supabase
            .from('profiles')
            .update({ status: newStatus })
            .eq('id', user?.id);
            
          if (profileError) {
            console.error('Error updating advisor status:', profileError);
          } else {
            console.log(`Updated advisor status to ${newStatus}`);
          }
        } else {
          console.log('Advisor has other active chats, keeping status as busy');
        }
      } catch (err) {
        console.error('Error updating advisor status after chat close:', err);
      }
    }
    
    setSelectedChat(null);
    setShowChatModal(false);
    
    // Refresh dashboard data to get updated chat requests
    fetchDashboardData();
  }, [user?.id, selectedChat]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const pendingChats = chatRequests.filter(chat => chat.status === 'pending');
  const activeChats = chatRequests.filter(chat => chat.status === 'active');

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-purple-900 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-purple-800 flex items-center justify-center overflow-hidden">
                {profile?.profileImageUrl ? (
                  <img 
                    src={profile.profileImageUrl} 
                    alt={profile.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-purple-300" />
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold">Welcome, {profile?.displayName}</h1>
                <p className="text-purple-200">Advisor Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {pendingChats.length > 0 && (
                <div className="relative">
                  <BellRing className="text-yellow-400 w-6 h-6 animate-bounce" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {pendingChats.length}
                  </span>
                </div>
              )}
              <button
                onClick={toggleAvailability}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  settings?.is_available
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-gray-500 hover:bg-gray-600'
                }`}
              >
                <Power className="w-5 h-5 mr-2" />
                {settings?.is_available ? 'Online' : 'Offline'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profile Settings
            </button>
          </div>
        </div>

        {notificationError && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex items-center">
              <BellRing className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm text-yellow-700">{notificationError}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-400" />
              <p className="ml-3 text-sm text-green-700">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {activeTab === 'dashboard' && (
          <>
            {pendingChats.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <BellRing className="text-yellow-500 w-6 h-6 mr-2 animate-pulse" />
                  Pending Chat Requests
                </h2>
                <div className="space-y-4">
                  {pendingChats.map(request => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{request.client_name}</h3>
                          <p className="text-sm text-gray-500">
                            Requested {new Date(request.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleChatAccept(request)}
                          disabled={request.isProcessing || activeChats.length > 0}
                          className={`px-4 py-2 rounded-lg text-white ${
                            request.isProcessing
                              ? 'bg-gray-400 cursor-not-allowed'
                              : activeChats.length > 0
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-green-500 hover:bg-green-600'
                          }`}
                          title={activeChats.length > 0 ? "You must finish your current chat before accepting a new one" : ""}
                        >
                          {request.isProcessing ? (
                            <div className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                              Processing...
                            </div>
                          ) : activeChats.length > 0 ? (
                            <div className="flex items-center">
                              <X className="w-5 h-5 mr-2" />
                              Busy
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <Check className="w-5 h-5 mr-2" />
                              Accept
                            </div>
                          )}
                        </button>
                        <button
                          onClick={() => handleChatDecline(request)}
                          disabled={request.isProcessing}
                          className={`px-4 py-2 rounded-lg text-white ${
                            request.isProcessing
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-red-500 hover:bg-red-600'
                          }`}
                        >
                          {request.isProcessing ? (
                            <div className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                              Processing...
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <X className="w-5 h-5 mr-2" />
                              Decline
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-6 flex flex-wrap gap-4">
              <button
                onClick={handleTestNotification}
                className="flex items-center px-4 py-2 rounded-lg text-white bg-purple-600 hover:bg-purple-700"
              >
                <BellRing className="h-5 w-5 mr-2" />
                Test Notifications
              </button>

              <NotificationSoundSelector 
                onSelect={handleSoundChange}
                currentSound={notificationSound}
              />

              <button
                onClick={() => setShowPricingModal(true)}
                className="flex items-center px-4 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700"
              >
                <DollarSign className="h-5 w-5 mr-2" />
                Update Pricing
              </button>
            </div>

            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">Your Current Pricing</h2>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <MessageCircle className="text-purple-600 w-8 h-8 mr-3" />
                  <div>
                    <p className="text-gray-500 text-sm">Message Rate</p>
                    <p className="text-2xl font-bold">${profile?.perMessageRate.toFixed(2)}/msg</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setNewPricing({
                      perMessageRate: profile?.perMessageRate || 0
                    });
                    setShowPricingModal(true);
                  }}
                  className="text-purple-600 hover:text-purple-800"
                >
                  Edit
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Total Earnings</h3>
                  <DollarSign className="text-purple-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${(stats?.totalEarnings || 0).toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Platform Fees: ${((stats?.totalEarnings || 0) * 0.2).toFixed(2)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Monthly Earnings</h3>
                  <TrendingUp className="text-green-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${(stats?.monthlyEarnings || 0).toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">Current Month</p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Total Readings</h3>
                  <Activity className="text-blue-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">{stats?.totalReadings || 0}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Avg Rating: {(stats?.rating || 0).toFixed(1)}⭐
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Active Users</h3>
                  <Users className="text-indigo-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">{stats?.activeClients || 0}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Total Clients: {stats?.totalClients || 0}
                </p>
              </div>
            </div>

            {activeChats.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Active Chats</h2>
                <div className="space-y-4">
                  {activeChats.map(chat => (
                    <div
                      key={chat.id}
                      className={`flex items-center justify-between p-4 ${
                        selectedChat?.id === chat.id
                          ? 'bg-purple-50 border border-purple-200'
                          : 'bg-gray-50 border border-gray-200'
                      } rounded-lg`}
                    >
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                          selectedChat?.id === chat.id
                            ? 'bg-purple-100'
                            : 'bg-gray-100'
                        }`}>
                          <User className={`w-6 h-6 ${
                            selectedChat?.id === chat.id
                              ? 'text-purple-600'
                              : 'text-gray-600'
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{chat.client_name}</h3>
                          <p className="text-sm text-gray-500">
                            Started {new Date(chat.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedChat(chat);
                            setShowChatModal(true);
                          }}
                          className={`px-4 py-2 rounded-lg text-white ${
                            selectedChat?.id === chat.id
                              ? 'bg-purple-600 hover:bg-purple-700'
                              : 'bg-gray-500 hover:bg-gray-600'
                          }`}
                        >
                          <div className="flex items-center">
                            <MessageCircle className="w-5 h-5 mr-2" />
                            {selectedChat?.id === chat.id ? 'Current Chat' : 'View Chat'}
                          </div>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'profile' && (
          <AdvisorProfile onProfileUpdate={handleProfileUpdate} />
        )}

        {showPricingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Update Pricing</h2>
                <button
                  onClick={() => setShowPricingModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Per Message Rate ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newPricing.perMessageRate}
                    onChange={(e) => setNewPricing(prev => ({
                      ...prev,
                      perMessageRate: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowPricingModal(false)}
                    className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePricing}
                    className="px-4 py-2 rounded-lg text-white bg-purple-600 hover:bg-purple-700"
                  >
                    Update Pricing
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ${
            selectedChat && showChatModal ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        >
          {selectedChat && user && (
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[80vh] flex flex-col">
              <div className="p-4 border-b flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{selectedChat.client_name}</h3>
                    <p className="text-sm text-gray-500">Active Chat</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowChatModal(false);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <ChatInterface
                  conversationId={selectedChat.id}
                  clientId={selectedChat.client_id}
                  advisorId={user.id}
                  isClient={false}
                  onClose={handleChatClose}
                />
              </div>
            </div>
          )}
        </div>

        {selectedChat && !showChatModal && (
          <div
            onClick={() => setShowChatModal(true)}
            className="fixed bottom-0 right-0 bg-purple-600 text-white p-4 rounded-tl-lg cursor-pointer z-40 flex items-center space-x-2"
          >
            <MessageCircle className="w-6 h-6" />
            <span>Chat with {selectedChat.client_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdvisorDashboard;